import React, { useState } from "react";
import { motion } from "framer-motion";
import dr1Img from "../../images/digital-readiness/dr1.png";

// ================= OPTIONS =================

const likertOptions = [
  { value: 1, label: "Strongly disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly agree" },
];

// ================= QUESTIONS =================

const QUESTIONS = [
  {
    id: 7,
    text: "I can create and edit Word, Excel, and PowerPoint documents.",
  },
  {
    id: 8,
    text: "I manage my digital files and folders systematically and efficiently for learning purposes.",
  },
];

const DRStep2 = () => {
  const student = JSON.parse(localStorage.getItem("student"));
  const studentId = student?.student_id;

  const [answers, setAnswers] = useState({
    7: null,
    8: null,
  });

  const [loading, setLoading] = useState(false);

  // ================= HANDLER =================

  const handleSelect = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const isValid = () => {
    return QUESTIONS.every((q) => answers[q.id]);
  };

  // ================= SUBMIT =================

  const handleNext = async () => {
    if (!studentId) {
      alert("Student session not found. Please log in again.");
      window.location.href = "/login";
      return;
    }

    if (!isValid()) {
      alert("Please answer all questions before continuing.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        student_id: Number(studentId),
        readiness_id: 2, // Computer Literacy
        next_step: 3,
        data: QUESTIONS.map((q) => ({
          item_id: q.id,
          rating: answers[q.id],
        })),
      };

      const res = await fetch(
        "http://localhost:3000/api/digital-readiness/save",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to save data.");
      }

      localStorage.setItem(
        "student",
        JSON.stringify({
          ...student,
          dr_current_step: result.next_step || 3,
        })
      );

      window.location.href = "/digital-readiness/step3";
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-700 to-blue-500 text-white flex flex-col items-center px-4 py-4">
      <div className="w-full max-w-5xl mb-2 text-xs text-blue-100">
        Step 2 of 6 – Digital Readiness Questionnaire
      </div>

      <div className="w-full max-w-5xl bg-white/10 rounded-2xl shadow-xl p-4 md:p-6 flex flex-col md:flex-row gap-5">
        {/* LEFT IMAGE */}
        <div className="md:w-2/5 flex justify-center items-center">
          <img
            src={dr1Img}
            alt="Digital readiness – computer literacy"
            className="w-full max-w-xs rounded-xl bg-white border shadow"
          />
        </div>

        {/* RIGHT CONTENT */}
        <div className="md:w-3/5">
          <h1 className="text-xl md:text-2xl font-bold text-yellow-300 mb-1">
            Digital Readiness – Computer Literacy
          </h1>
          <p className="text-xs md:text-sm text-blue-100 mb-3">
            Please indicate how much you agree with each statement.
          </p>

          <div className="space-y-4">
            {QUESTIONS.map((q, idx) => (
              <QuestionBlock
                key={q.id}
                title={`${idx + 1}. ${q.text}`}
              >
                <div className="flex gap-1">
                  {likertOptions.map((opt) => (
                    <OptionButton
                      key={opt.value}
                      selected={answers[q.id] === opt.value}
                      onClick={() =>
                        handleSelect(q.id, opt.value)
                      }
                      label={opt.label}
                    />
                  ))}
                </div>
              </QuestionBlock>
            ))}
          </div>

          <div className="flex justify-between mt-4">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() =>
                (window.location.href =
                  "/digital-readiness/DRStep1")
              }
              className="bg-blue-800 border border-blue-300 text-white px-6 py-2 rounded-full text-xs"
            >
              ← Back
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              disabled={loading}
              onClick={handleNext}
              className="bg-yellow-300 text-blue-900 font-bold px-6 py-2 rounded-full shadow disabled:bg-gray-400"
            >
              {loading ? "Saving..." : "Next →"}
            </motion.button>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[10px] text-blue-200">
        © 2025 Universitas Terbuka
      </p>
    </div>
  );
};

// ================= REUSABLE UI =================

const QuestionBlock = ({ title, children }) => (
  <div className="bg-blue-800/70 rounded-xl p-3 border border-blue-500/60">
    <p className="text-sm font-semibold mb-2">{title}</p>
    {children}
  </div>
);

const OptionButton = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 py-2 rounded-lg border text-[10px] md:text-xs transition ${
      selected
        ? "bg-yellow-300 text-blue-900 border-yellow-400 font-bold"
        : "bg-blue-700 text-white border-blue-400 hover:bg-blue-600"
    }`}
  >
    {label}
  </button>
);

export default DRStep2;
